import styled from '@emotion/styled'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAudioSrc, playInterrupt } from '../../lib/player'
import { handleBrokenBandcampUrl } from '../../lib/viewHelpers'

import { MdClose as Close } from 'react-icons/md'

import {
  playerClose,
  playerNext,
  playerPause,
  playerPlay,
  playerPrevious,
} from '../../redux/actions'

import FixedFooter from '../FixedFooter'
import { AudioContext } from '../PlayerWrapper'
import MediaSession from './MediaSession'
import PlayerContents from './PlayerContents'
import PlayerError from './PlayerError'

const CloseButton = styled(Close)`
  cursor: pointer;
  position: absolute;
  top: 4px;
  right: -3px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.space[2]};
  margin-left: ${({ theme }) => theme.space[1]};
  margin-right: ${({ theme }) => theme.space[1]};
  position: relative;

  ${({ theme }) => theme.media.mUp} {
    flex-direction: row;
    margin-bottom: 0;
  }
`

const Player = () => {
  const audioRef = useContext(AudioContext)
  const dispatch = useDispatch()
  const { error, open, playIndex, playing, playingListId, playlist, volume } =
    useSelector((state) => state.player)
  const [progress, setProgress] = useState(0)
  const intervalRef = useRef(null)

  const currentTrack = playlist.length > 0 ? playlist[playIndex] : {}
  const { artist, cover, title } = currentTrack
  const duration = audioRef.current.duration
  const hasMediaSession =
    window && window.navigator && 'mediaSession' in window.navigator

  useEffect(() => {
    // Pause and clean up on unmount
    return () => {
      audioRef.current.pause()
      clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (!playing) {
      audioRef.current.pause()
      clearInterval(intervalRef.current)
    }
  }, [playing])

  // Handle setup when changing tracks
  useEffect(() => {
    // Don't call on initial state load when playIndex == -1
    if (playIndex > -1) {
      setProgress(0)
      let src = getAudioSrc({ playIndex, playlist })
      if (!src) {
        handleBrokenBandcampUrl({
          handleRemoved: () => {
            next()
          },
          handleUpdated: (updatedData) => {
            if (updatedData.streamURL) {
              src = updatedData.streamURL
            } else {
              next()
            }
          },
          url: playlist[playIndex].cover,
        }).then(() => {
          audioRef.current.src = src
          play()
        })
      } else {
        audioRef.current.src = src
        play()
      }
    }
  }, [playIndex, playingListId])

  useEffect(() => {
    audioRef.current.volume = volume
  }, [volume])

  if (!audioRef.current) return null

  const close = () => {
    audioRef.current.pause()
    clearInterval(intervalRef.current)
    setProgress(0)
    dispatch(playerClose())
  }

  const next = () => {
    dispatch(playerNext())
  }

  const onScrubEnd = () => {
    // If not already playing, start
    if (!playing) {
      play()
    }
    startTimer()
  }

  const pause = () => {
    clearInterval(intervalRef.current)
    dispatch(playerPause())
    audioRef.current.pause()
  }

  const play = () => {
    dispatch(playerPlay())
    playInterrupt(audioRef.current)
    startTimer()
  }

  const previous = () => {
    dispatch(playerPrevious())
  }

  const scrub = (value) => {
    // Clear any timers already running
    clearInterval(intervalRef.current)
    audioRef.current.currentTime = value
    setProgress(audioRef.current.currentTime)
  }

  const defaultSkipTime = 30

  const seekBackward = () => {
    scrub(audioRef.current.currentTime - defaultSkipTime)
  }

  const seekForward = () => {
    scrub(audioRef.current.currentTime + defaultSkipTime)
  }

  const startTimer = () => {
    // Clear any timers already running
    clearInterval(intervalRef.current)

    intervalRef.current = setInterval(() => {
      if (audioRef.current.ended) {
        next()
      } else {
        setProgress(audioRef.current.currentTime)
      }
    }, [1000])
  }

  if (!open) return null

  return (
    <FixedFooter>
      <Container id="player">
        {hasMediaSession && !error && (
          <MediaSession
            artist={artist}
            artwork={cover.replace('.jpg', '.png')}
            onPause={play}
            onNextTrack={next}
            onPlay={play}
            onPreviousTrack={previous}
            onSeekBackward={seekBackward}
            onSeekForward={seekForward}
            title={title}
          />
        )}
        {error ? (
          <PlayerError error={error} />
        ) : (
          <PlayerContents
            duration={duration}
            next={next}
            onScrubEnd={onScrubEnd}
            pause={pause}
            play={play}
            previous={previous}
            progress={progress}
            scrub={scrub}
          />
        )}
        <CloseButton onClick={close} />
      </Container>
    </FixedFooter>
  )
}

export default Player
